// src/pages/relatorios/LigacoesPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Form, InputGroup, Row, Col, Spinner, Alert, Pagination, Badge } from 'react-bootstrap';
import { FaSearch, FaPrint } from 'react-icons/fa';
import useSortable from '../../hooks/useSortable';
import SortableTh from '../../components/SortableTh';
import api from '../../service/api';

interface Ligacao {
  LIG_CODIGO: number;
  PES_CODIGO: number;
  LIG_HIDROMETRO: string;
  LIG_ENDERECO: string;
  LIG_SITUACAO: string;
  LIG_FICHA: number;
  LIG_TAXAFIXA: number;
  LIG_CONTAS_AGRUPADAS: number;
  PES_NOME?: string;
}

interface Pessoa {
  PES_CODIGO: number;
  PES_NOME: string;
}

const ITEMS_PER_PAGE = 15;

const getSituacaoBadge = (s: string) => {
  const sit = (s || '').trim();
  if (sit === 'Ativo') return <Badge bg="success">Ativo</Badge>;
  if (sit === 'Inativo') return <Badge bg="secondary">Inativo</Badge>;
  return <Badge bg="warning">{s}</Badge>;
};

const RelLigacoesPage: React.FC = () => {
  const [ligacoes, setLigacoes] = useState<Ligacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSituacao, setFilterSituacao] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [ligRes, pesRes] = await Promise.all([api.get('/ligacoes'), api.get('/pessoas')]);
      const pessoasMap = new Map<number, string>(pesRes.data.map((p: Pessoa) => [p.PES_CODIGO, p.PES_NOME]));
      const enriched = ligRes.data.map((l: Ligacao) => ({ ...l, PES_NOME: pessoasMap.get(l.PES_CODIGO) ?? '' }));
      setLigacoes(enriched.sort((a: Ligacao, b: Ligacao) =>
        (a.PES_NOME || '').localeCompare(b.PES_NOME || '', 'pt-BR')
      ));
    } catch {
      setError('Não foi possível carregar os dados.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = ligacoes.filter(l => {
    const situacao = (l.LIG_SITUACAO || '').trim();
    return (l.LIG_ENDERECO?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.PES_NOME?.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (filterSituacao === '' || situacao === filterSituacao);
  });
  const { sortedData: sortedLigacoes, sortKey, sortDir, handleSort } = useSortable<Ligacao>(filtered, 'PES_NOME');
  const paginated = sortedLigacoes.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Relatório de Ligações</h1>
        <Button variant="outline-secondary" onClick={() => window.print()}>
          {FaPrint({ className: "me-2" })} Imprimir
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm">
        <Card.Body>
          <Row className="mb-3">
            <Col md={5}>
              <InputGroup>
                <InputGroup.Text>{FaSearch({})}</InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por endereço ou pessoa..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select value={filterSituacao} onChange={e => { setFilterSituacao(e.target.value); setCurrentPage(1); }}>
                <option value="">Todas as situações</option>
                <option value="Ativo">Ativo</option>
                <option value="Inativo">Inativo</option>
              </Form.Select>
            </Col>
            <Col md={4} className="d-flex justify-content-md-end align-items-center">
              <span className="text-muted">Total: {filtered.length} ligações</span>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : filtered.length === 0 ? (
            <Alert variant="info">Nenhuma ligação encontrada.</Alert>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover size="sm">
                  <thead className="table-light">
                    <tr>
                      <SortableTh<Ligacao> label="Código" sortKey="LIG_CODIGO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Ligacao> label="Pessoa" sortKey="PES_NOME" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Ligacao> label="Hidrômetro" sortKey="LIG_HIDROMETRO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Ligacao> label="Endereço" sortKey="LIG_ENDERECO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Ligacao> label="Ficha" sortKey="LIG_FICHA" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Ligacao> label="Situação" sortKey="LIG_SITUACAO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map(l => (
                      <tr key={l.LIG_CODIGO}>
                        <td>{l.LIG_CODIGO}</td>
                        <td>{l.PES_NOME}</td>
                        <td>{l.LIG_HIDROMETRO}</td>
                        <td>{l.LIG_ENDERECO}</td>
                        <td>{l.LIG_FICHA}</td>
                        <td>{getSituacaoBadge(l.LIG_SITUACAO)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                      .map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {arr[idx - 1] && p - arr[idx - 1] > 1 && <Pagination.Ellipsis disabled />}
                          <Pagination.Item active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
                        </React.Fragment>
                      ))}
                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default RelLigacoesPage;
